import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from "react-router-dom";
import { apiAgent } from '../../../axiosConfig.js';
import { Link } from 'react-router-dom';
import OtpVerification from '../../../PreAuthRender/verification/OtpVerification.js';
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx';
import { getRelevantSsoConfig } from '../../utils/helper_utils.js';
import ConditionalRender from "../../../ConditionalRender.js";
import getApiUrl from '../../utils/UrlHelper.js';
import { FRONTEND_CONFIG } from '../../../configurations/application_config.js';
import { PasswordBox } from '../../../design_system/components/Input Fields/PasswordBox.jsx';
import { InputBox } from '../../../design_system/components/Input Fields/InputBox.jsx';
import { Button } from '../../../design_system/components/Button/Button.jsx';
import SelectionControl from '../../../design_system/components/SelectionControls/SelectionControl.jsx';
import { Loader } from '../../../design_system/components/Loader.jsx';

const scalekit_redirect_uri = `${FRONTEND_CONFIG["WEB_URL"]}login`

function getAuthorizationUrl(redirectUri, options = {}) {
  const SK_CLIENT_ID = "prd_skc_75025996947980805";
  const SK_ENV_URL = 'https://sso.almaconnect.com';
  const SK_AUTHORIZATION_ENDPOINT = 'oauth/authorize';

  const params = new URLSearchParams({
    redirect_uri: redirectUri,
    client_id: SK_CLIENT_ID,
    response_type: 'code',
    scope: 'openid profile email',
    login_hint: options.email || '',
    state: options.email || '',
  });

  return `${SK_ENV_URL}/${SK_AUTHORIZATION_ENDPOINT}?${params.toString()}`;
}

function LoginScreen({ onLoginSuccess }) {

  const [email, setEmail] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [errors, setErrors] = useState({});

  const location = useLocation();
  const history = useHistory();
  const toast = useToast();

  useEffect(() => {
    let searchParams = new URLSearchParams(window.location.search);
    let state = searchParams.get("state");
    let code = searchParams.get("code");
    if (code) {
      setEmail(state);
      setIsLoading(true);
      authenticateWithSSOCode(code);
    } else {
      setPageLoading(false)
    }
  }, [location, history]);


  const authenticateWithSSOCode = (code) => {
    apiAgent({
      method: "get",
      url: getApiUrl("scalekit_authenticate"),
      params: { code: code, redirect_uri: scalekit_redirect_uri }
    }).then(res => {
      setIsLoading(false)
      let redirect_url = res.data.redirect_url
      window.location.href = redirect_url
    }).catch(err => {
      console.log(err);
      setIsLoading(false)
    })
  }

  const enablePassword = () => {
    setEmailLocked(true)
    setPasswordEnabled(true)
    setIsLoading(false);
  }

  const onEmailInput = (e) => {
    setEmail(e.target.value);
    setErrors((prev) => ({ ...prev, email: '' }));
    setPasswordEnabled(false)
  };

  const onEditEmail = () => {
    setEmailLocked(false)
    setPasswordEnabled(false)
    setPassword(null)
  }
  const onPasswordInput = (e) => {
    setPassword(e.target.value);
    setErrors((prev) => ({ ...prev, password: '' }));
  };

  const loginWithPassword = () => {
    apiAgent({
      method: 'post',
      url: '/users/login',
      data: {
        email: email.toLowerCase(),
        password,
        remember_me: rememberMe,
      },
    }).then(
      (response) => {
        setIsLoading(false);
        if (response.data.two_fa_enabled) {
          setTwoFaEnabled(true);
        } else {
          onLoginSuccess(response.data);
        }
      },
      (error) => {
        setIsLoading(false);
        const serverErrors = error.response?.data?.server_message || {};
        const otpLimitError = serverErrors?.otp_attemps_exceed || serverErrors?.wrong_otp_attemps_exceed;

        if (error.response?.data) {
          setErrors({ password: error.response.data?.error || serverErrors?.password_error });
        }

        if (otpLimitError) {
          toast.warning(otpLimitError, { position: 'bottom-start' });
        }
      }
    );
  }
  const onFormSubmit = () => {
    let hasError = false;
    const newErrors = {};

    if (!email) {
      newErrors.email = 'No account found for this email\n';
      hasError = true;
    }

    if (!password && passwordEnabled) {
      newErrors.password = 'Please enter Password.';
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    setIsLoading(true);
    if (!passwordEnabled) {
      apiAgent({
        method: "get",
        url: getApiUrl("scalekit_initialize_auth"),
        params: { email: email, redirect_uri: scalekit_redirect_uri }
      }).then(res => {
        if (res.data.auth_response.has_error) {
          enablePassword()
        } else if (res.data.auth_response.redirect_url) {
          window.location.replace(res.data.auth_response.redirect_url)
        } else {
          enablePassword()
        }
      }).catch(err => {
        enablePassword()
      })
    } else {
      loginWithPassword()
    }
  };
  if (pageLoading) {
    return (
      <Loader isLoading={true} Size="Large" type="Primary" isFullPage={true} />
    );
  }
  return (
    <>
      {twoFaEnabled ? (
        <OtpVerification user_info={{ email: email.toLowerCase() }} onLoginSuccess={onLoginSuccess} />
      ) : (
        <div className="login-container">
          <div className="headline-l-bold login-title text-center">
            {passwordEnabled ? "Enter your password" : "Log in to your account"}
          </div>

          <div className="login-email-group mt-xl relative">
            <ConditionalRender if={emailLocked}>
              <div className="link-default body-s-bold login-edit-action absolute"
                onClick={onEditEmail}
              >
                Edit
              </div>
            </ConditionalRender>
            <InputBox
              label="Email"
              showLabel={true}
              value={email}
              Size="Large"
              handleOnChange={onEmailInput}
              placeholder="johndoe@domain.com"
              disabled={emailLocked}
              error={errors.email}
              helperText={errors.email}
              showHelperText={true}
            />
          </div>

          <ConditionalRender if={passwordEnabled}>
            <div className="login-password-group mt-l">
              <PasswordBox
                value={password}
                Size="Large"
                handleOnChange={onPasswordInput}
                placeholder="Enter your password"
                tooltipEnable={false}
                helperText={errors.password}
                showHelperText={true}
                label="Password"
                showLabel={true}
                error={errors.password}
              />
            </div>
            <div className="body-s-bold login-forgot-password mt-s">
              <Link to="/forgot_password" className="link-default">
                Forgot password?
              </Link>
            </div>
          </ConditionalRender>


          <div className="login-action-group mt-l">
            <Button
              Size="Medium"
              onClick={onFormSubmit}
              error={errors?.button}
              disabled={errors?.button}
              isLoading={isLoading}
              buttonStyle={{ width: '100%' }}
            >
              Continue
            </Button>
          </div>
          <ConditionalRender if={passwordEnabled}>
            <div className="login-remember-me mt-l">
              <SelectionControl
                type="checkbox"
                label="Remember me for 30 days"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              />
            </div>
          </ConditionalRender>


          <div className="body-s-semibold login-signup-prompt text-center mt-l">
            Don’t have an account?{' '}
            <a href="/signup" className="link-default">
              Sign up
            </a>
          </div>

        </div>
      )}
    </>
  );
}

export default LoginScreen;
export { getAuthorizationUrl }